"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyPropsDiff = exports.getPropsDiff = exports.deletionSentinel = void 0;
const json_1 = require("./json");
exports.deletionSentinel = '__DELETE_THIS_PROP__';
/**
 * Creates a diff object that can be used to apply overrides to grouped module's props.
 * Typically, the Layout editor will present the group default props and then this function
 * is used to describe any changes the author makes to that specific instance.
 * Note: The way that diffs are created in the Layout Editor, there is no way to affirm a
 * specific value that is present in the default props should forever stay the same. Eg, if the
 * default value is "A", one cannot force a value of "A" in the diff to guard against future
 * changes to the default, though data-wise it is possible.
 * Note: JSON.stringify and JSON.parse are used liberally here because they're known to be very
 * fast, their usage is terse, and we're not dealing with large objects.
 * @param base JSONObject that can be overridden.
 * @param altered The output of the props form, with the result of any changes made
 * by the content editor for a given grouped (staff or boss) module.
 * @param depth How many levels of recursion this invocation is operating at. This is
 * likely exclusively relevant only for depth === 0.
 * @returns A diff object that can be used to apply overrides to grouped module's props.
 */
const getPropsDiff = (base, altered, depth = 0) => {
    const diff = Object.entries(altered).reduce((acc, [key, alteredValue]) => {
        const baseValue = base[key];
        if (typeof alteredValue === 'undefined') {
            // Do nothing. This empty case reduces the check count later.
        }
        else if (typeof baseValue === 'undefined') {
            // This key exists only in the altered version, so add it to the diff.
            acc[key] = alteredValue;
        }
        else if (depth === 0 && key === 'styleAttr') {
            // This is the `styleAttr`, which is a special value that, when applying the
            // diff, will be concatenated (with a semicolon) since it's CSS.
            acc[key] = alteredValue;
        }
        else if (
        // Exact same primitive value
        alteredValue === baseValue ||
            // Effectively the same value (usually Arrays)
            JSON.stringify(alteredValue) === JSON.stringify(baseValue)) {
            // Do nothing. This empty case reduces the check count later.
        }
        else if ((0, json_1.isJSONObject)(alteredValue) && (0, json_1.isJSONObject)(baseValue)) {
            // Both are objects, so recurse.
            acc[key] = (0, exports.getPropsDiff)(baseValue, alteredValue, depth + 1);
        }
        else {
            // Add to the override since at least one is a non-JSONObject.
            acc[key] = alteredValue;
        }
        return acc;
    }, {});
    // Mark keys in the base that were manually removed in the altered version.
    Object.keys(base).forEach((key) => {
        if (!(key in altered) && key !== 'styleAttr') {
            diff[key] = exports.deletionSentinel;
        }
    });
    // With depth === 0, clone and remove empty objects. Clone because objects
    // and arrays could be references.
    return depth > 0
        ? diff
        : (0, json_1.pruneEmptyObjects)(JSON.parse(JSON.stringify(diff))) ?? {};
};
exports.getPropsDiff = getPropsDiff;
/**
 * Returns a new object equivalent to the base object with the diff applied.
 * @param base JSONValue that can be overridden.
 * @param diff The override diff object.
 * @param isForFormDisplay For values that need operations other than replacement, the
 * form needs to apply the diff using different rules. At the time of creating this
 * function `styleAttr` was the only such value, but others could exist later.
 * @param depth How many levels of recursion this invocation is operating at. This is
 * likely exclusively relevant only for depth === 0.
 * @returns The overridden props for either displaying the module or populating the editing form.
 */
const applyPropsDiff = (base, diff, isForFormDisplay = false, depth = 0) => {
    if (!diff)
        return JSON.parse(JSON.stringify(base));
    if (base === exports.deletionSentinel || !base) {
        return JSON.parse(JSON.stringify(diff));
    }
    const altered = JSON.parse(JSON.stringify(base));
    if (!(0, json_1.isJSONObject)(base) || !(0, json_1.isJSONObject)(diff))
        return altered;
    // `altered` for the form should contain no trace of `base.styleAttr`, only
    // the diff's version of `base.styleAttr`, so delete it. If there's a value
    // present in the diff, it will be added in a later operation.
    if (depth === 0 && isForFormDisplay && !('styleAttr' in diff)) {
        delete altered.styleAttr;
    }
    const processed = Object.entries(diff).reduce((acc, [key, value]) => {
        const baseValue = base[key] ?? null;
        if (key === 'styleAttr' && !isForFormDisplay) {
            // When applying the diff for the attendee view, concatenated (with
            // a semicolon) since it's CSS.
            acc[key] = `${baseValue ?? ''};${value ?? ''}`;
        }
        else if (key === 'styleAttr' && isForFormDisplay) {
            // When applying the diff for the editing form, use the diff's version.
            // Casting [unlikely] falsey values to ensure the CSS stays valid.
            acc[key] = value || '';
        }
        else if (value === exports.deletionSentinel) {
            delete acc[key];
        }
        else if (value &&
            baseValue &&
            (0, json_1.isJSONObject)(value) &&
            (0, json_1.isJSONObject)(baseValue)) {
            acc[key] = (0, exports.applyPropsDiff)(baseValue, value, isForFormDisplay, depth + 1);
        }
        else {
            // All other cases, just set the value. Notably, including Arrays.
            acc[key] = value;
        }
        return acc;
    }, altered);
    return depth > 0 ? processed : JSON.parse(JSON.stringify(processed));
};
exports.applyPropsDiff = applyPropsDiff;
